import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CloudDownload as CloudDownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Dashboard as DashboardIcon,
  Create,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { 
  getAllowedLabTypes, 
  getLabTypeName, 
  getInitialLabTypeId,
  getFirstLabTypeId, 
  shouldDisableDropdown,
  LAB_TYPES 
} from "../../../../utils/labTypeFilter";

const DisciplineMaster = () => {
  // Get filtered lab types using utility
  const filteredLabTypes = getAllowedLabTypes(LAB_TYPES);
  
  // Debug: Log the filtered lab types
  console.log('DisciplineMaster - filteredLabTypes:', filteredLabTypes);
  console.log('DisciplineMaster - shouldDisableDropdown:', shouldDisableDropdown(filteredLabTypes));

  // Helper function to get lab type name by ID
  const getLabTypeNameLocal = (labTypeId) => {
    return getLabTypeName(labTypeId, filteredLabTypes);
  };

  // Helper function to get lab type ID by name
  const getLabTypeIdByName = (labTypeName) => {
    if (!labTypeName) return null;
    const labType = filteredLabTypes.find(
      (l) => l.name.toLowerCase() === labTypeName.toLowerCase()
    );
    return labType ? labType.id : null;
  };

  const [formData, setFormData] = useState({
    id: null,
    disciplineName: "",
    labTypeId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterLabType, setFilterLabType] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchDisciplines();
  }, []);

  // Get labTypeId from localStorage and set in form data
  useEffect(() => {
    const initialLabTypeId = getInitialLabTypeId();
    const firstLabTypeId = getFirstLabTypeId(); // Get first available lab type for filter default
    
    console.log('Initialization - initialLabTypeId:', initialLabTypeId);
    console.log('Initialization - firstLabTypeId for filter:', firstLabTypeId);
    
    if (initialLabTypeId) {
      const labTypeName = getLabTypeNameLocal(initialLabTypeId);
      console.log(`Initial Lab Type ID: ${initialLabTypeId}, Lab Type: ${labTypeName}`);
      
      setFormData(prev => ({ 
        ...prev, 
        labTypeId: initialLabTypeId 
      }));
    }
    
    // Set filter to first available lab type (instead of empty/all)
    if (firstLabTypeId) {
      setFilterLabType(firstLabTypeId);
      console.log('Set labTypeFilter to first available:', firstLabTypeId);
    } else {
      console.log('No lab type IDs found for filter');
    }
  }, []);

  const fetchDisciplines = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-master/disciplines`,
      );
      if (res.data.status === "success") {
        // Transform API data: convert labType string name to labTypeId number
        const transformedData = (res.data.data || []).map(item => {
          const labTypeId = getLabTypeIdByName(item.labType);
          return {
            ...item,
            labTypeId: labTypeId || item.labTypeId, // Use converted ID or fallback to existing
          };
        });
        console.log('Fetched disciplines:', transformedData);
        setDisciplines(transformedData);
      } else {
        setError(res.data.message || "Failed to fetch Disciplines");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching Disciplines");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.disciplineName || !formData.labTypeId) {
      setError("All fields are required");
      return;
    }
    setError("");
    setLoading(true);
    try {
      if (formData.id) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_API_URL}/admin-master/disciplines/${formData.id}`,
          {
            labTypeId: formData.labTypeId,
            disciplineName: formData.disciplineName,
            updatedById: localStorage.getItem('orgId') || 0,
          },
        );
        setSuccessMessage("Discipline Master Updated Successfully");
      } else {
        // Create
        await axios.post(
          `${process.env.REACT_APP_API_URL}/admin-master/disciplines`,
          {
            labTypeId: formData.labTypeId,
            disciplineName: formData.disciplineName,
            createdById: localStorage.getItem('orgId') || 0,
          },
        );
        setSuccessMessage("Discipline Master Added Successfully");
      }
      fetchDisciplines();
      setTimeout(() => setSuccessMessage(""), 3000);
      handleReset();
    } catch (err) {
      setError(err.response?.data?.message || "Error saving Discipline");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    const labTypeId = row.labTypeId || getLabTypeIdByName(row.labType);
    setFormData({
      id: row.id,
      disciplineName: row.disciplineName,
      labTypeId: labTypeId,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin-master/disciplines/${deleteId}`,
      );
      setSuccessMessage("Discipline deleted successfully");
      fetchDisciplines();
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting Discipline");
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(""), 2000);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleReset = () => {
    const storedLabTypeId = localStorage.getItem('labTypeId');
    setFormData({
      id: null,
      disciplineName: "",
      labTypeId: storedLabTypeId ? Number(storedLabTypeId) : "",
    });
    setError("");
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API endpoint
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-master/disciplines/export`,
        {
          params: {
            labTypeId: filterLabType || undefined,
          },
        }
      );

      let dataToExport = [];
      if (res.data.status === "success" && res.data.data) {
        dataToExport = res.data.data;
      } else {
        // If API doesn't have export endpoint, use current data
        dataToExport = disciplines.filter(
          (row) =>
            !filterLabType ||
            String(row.labTypeId) === String(filterLabType)
        );
      }

      if (dataToExport.length === 0) {
        setError("There is no data available in this list.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      // Transform data for Excel
      const excelData = dataToExport.map((item, idx) => ({
        "Sr. No.": idx + 1,
        "Discipline Name": item.disciplineName,
        "Lab Type": item.labType || "Unknown",
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Disciplines");
      
      // Auto-size columns
      const maxWidth = 50;
      const colWidths = [
        { wch: 10 },
        { wch: 30 },
        { wch: 20 },
      ];
      ws['!cols'] = colWidths;
      
      XLSX.writeFile(wb, "Discipline_Master.xlsx");
      setSuccessMessage("Excel downloaded successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError("Error downloading Excel file");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", p: 1 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Gradient Header */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            <DashboardIcon
              sx={{ fontSize: "2rem", mr: 2, verticalAlign: "middle" }}
            />
            Discipline Master
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Manage Disciplines efficiently
          </Typography>
        </Paper>
        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage("")}
          >
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {/* Form Card */}
        <Card elevation={3} sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <DashboardIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                Add / Edit Discipline
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                mb: 2,
              }}
            >
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Lab Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Select
                      fullWidth
                      name="labTypeId"
                      value={formData.labTypeId}
                      onChange={handleInputChange}
                      displayEmpty
                      required
                      disabled={shouldDisableDropdown(filteredLabTypes)}
                      variant="outlined"
                    >
                      <MenuItem value="" disabled>
                        <em>--Select Lab Type--</em>
                      </MenuItem>
                      {filteredLabTypes.map((lab) => (
                        <MenuItem key={lab.id} value={lab.id}>
                          {lab.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Discipline Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="disciplineName"
                      value={formData.disciplineName}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="Enter Discipline Name"
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    gap: 3,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                    sx={{ backgroundColor: "#1976d2" }}
                  >
                    {formData.id ? "Update" : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                </Box>
              </form>
            </Box>
          </CardContent>
        </Card>
        {/* Table Card */}
        <Card elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <DashboardIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                Discipline List
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              startIcon={<CloudDownloadIcon />}
              onClick={handleDownloadExcel}
              disabled={loading}
              sx={{ minWidth: 160 }}
            >
              Export Excel
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Select
                fullWidth
                displayEmpty
                disabled={shouldDisableDropdown(filteredLabTypes)}
                value={filterLabType}
                onChange={(e) => {
                  setFilterLabType(e.target.value);
                  setPage(0);
                }}
                renderValue={(selected) => {
                  if (!selected) return <em>--All Lab Types--</em>;
                  const labType = filteredLabTypes.find((l) => l.id === selected);
                  return labType?.name || 'Unknown';
                }}
              >
                <MenuItem value="">
                  <em>--All Lab Types--</em>
                </MenuItem>
                {filteredLabTypes.map((lab) => (
                  <MenuItem key={lab.id} value={lab.id}>
                    {lab.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              Loading...
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ width: "10%" }}>
                        <strong>Sr. No.</strong>
                      </TableCell>
                      <TableCell sx={{ width: "15%" }}>
                        <strong>Action</strong>
                      </TableCell>
                       <TableCell sx={{ width: "30%" }}>
                        <strong>Lab Type</strong>
                      </TableCell>
                      <TableCell sx={{ width: "45%" }}>
                        <strong>Discipline Name</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {disciplines.filter(
                      (row) =>
                        !filterLabType ||
                        String(row.labTypeId) === String(filterLabType),
                    ).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">
                            No Discipline data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      disciplines
                        .filter(
                          (row) =>
                            !filterLabType ||
                            String(row.labTypeId) === String(filterLabType),
                        )
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row, idx) => {
                          const labType = filteredLabTypes.find(
                            (l) => String(l.id) === String(row.labTypeId),
                          );
                          return (
                            <TableRow key={row.id}>
                              <TableCell>
                                {page * rowsPerPage + idx + 1}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(row)}
                                  title="Update"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(row.id)}
                                  title="Delete"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                               <TableCell>
                                {labType?.name || row.labType || "Unknown"}
                              </TableCell>
                              <TableCell>{row.disciplineName}</TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={
                  disciplines.filter(
                    (row) =>
                      !filterLabType ||
                      String(row.labTypeId) === String(filterLabType),
                  ).length
                }
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                sx={{ borderTop: "1px solid #e0e0e0" }}
              />
            </>
          )}
        </Card>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Do you want to Delete this Discipline?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={loading}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default DisciplineMaster;
